/*
 * AppStoreBadges — official Apple App Store + Google Play download badges.
 *
 * Uses each platform's OFFICIAL badge image hosted by Apple / Google (per
 * their marketing brand guidelines). No local assets required.
 *
 * ── App Store compliance note ─────────────────────────────────────────────
 * These badges are only rendered on WEB. Inside the wrapped mobile builds
 * (iOS WKWebView / Android TWA) we hide them entirely. Rationale:
 *   • Inside the iOS build → the user already has the app; showing a
 *     Google Play badge violates Apple Guideline 3.1.1 (anti-steering) and
 *     showing an Apple badge is redundant. Hide both.
 *   • Inside the Android build → same logic in reverse.
 * Only the web audience (selahdownloads.com visitors) sees the badges.
 * ─────────────────────────────────────────────────────────────────────────
 */
import React from 'react';
import { getPlatformTarget } from '../utils/platform';

// ── Official store URLs ──────────────────────────────────────────────────
export const APP_STORE_URL =
  'https://apps.apple.com/us/app/selah-downloads/id6797657014';
export const GOOGLE_PLAY_URL =
  'https://play.google.com/store/apps/details?id=com.selahdownloads.app';

// ── Official badge images (hosted by Apple / Google) ──────────────────────
// Apple: Marketing tools API, stable for years, recommended by Apple.
// Google: Official Play badge CDN, stable for years, recommended by Google.
const APPLE_BADGE_SRC =
  'https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83';
const GOOGLE_BADGE_SRC =
  'https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png';

/**
 * Renders the pair of official App Store + Google Play badges.
 *
 * Props:
 *   • size      — 'sm' (h-10) | 'md' (h-12) | 'lg' (h-14). Default 'md'.
 *   • align     — 'start' | 'center'. Default 'start'.
 *   • className — additional wrapper classes.
 *   • hideOnWrappedBuilds (bool) — when true (default) we hide the badges
 *                                   inside iOS/Android wrapped builds.
 */
const AppStoreBadges = ({
  size = 'md',
  align = 'start',
  className = '',
  hideOnWrappedBuilds = true,
}) => {
  // Platform gating — hide inside wrapped mobile builds (compliance).
  if (hideOnWrappedBuilds) {
    const target = getPlatformTarget();
    if (target === 'ios' || target === 'android') return null;
  }

  const heightClass =
    size === 'sm' ? 'h-10' : size === 'lg' ? 'h-14' : 'h-12';
  const justifyClass =
    align === 'center' ? 'justify-center' : 'justify-start';

  return (
    <div
      className={`flex flex-wrap items-center gap-3 sm:gap-4 ${justifyClass} ${className}`}
      data-testid="app-store-badges"
    >
      {/* Apple App Store */}
      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Download Selah Downloads on the App Store"
        className="inline-block transition-transform duration-200 hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sd-primary)] focus-visible:ring-offset-2 rounded-lg"
        data-testid="app-store-badge-apple"
      >
        <img
          src={APPLE_BADGE_SRC}
          alt="Download on the App Store"
          className={`${heightClass} w-auto`}
          loading="lazy"
        />
      </a>

      {/* Google Play */}
      <a
        href={GOOGLE_PLAY_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Get Selah Downloads on Google Play"
        className="inline-block transition-transform duration-200 hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sd-primary)] focus-visible:ring-offset-2 rounded-lg"
        data-testid="app-store-badge-google"
      >
        <img
          src={GOOGLE_BADGE_SRC}
          alt="Get it on Google Play"
          /* Google Play badges have built-in padding — bump height slightly
             so the visual mark matches Apple's optical size. */
          className={`${
            size === 'sm' ? 'h-[3.25rem]' : size === 'lg' ? 'h-[4.5rem]' : 'h-[3.75rem]'
          } w-auto -my-2`}
          loading="lazy"
        />
      </a>
    </div>
  );
};

export default AppStoreBadges;
